<template>
  <div class="release-notes-handler">

    <!-- Essential features notes -->
    <transition name="fade">
      <div v-if="displayEssentialNotes" class="dark-overlay v-centerer essential-features">
        <div class="dark-overlay-content xlarge">
          <CloseButton @click.native="closeEssentialFeatures" />
          <!-- Title -->
          <h2 class="basic-sub-title">
            {{ $t('release.LATEST_ESSENTIAL_FEATURES') }}
            <Icon>{{ giftBold }}</Icon>
          </h2>
          <!-- Content -->
          <div class="notes-wrapper">
            <!-- eslint-disable vue/no-v-html -->
            <div v-for="(feature, idx) in latestReleaseEssentials" :key="idx" class="notes-wrapper__note">
              <div v-if="idx > 0" class="sep sep--centered" />
              <!-- NOTE: Re-call '$t' for specific i18n features, such as '@:...' or 'sth.uppercase', etc -->
              <div class="feature-title">
                <span>
                  <span v-html="feature.title" />
                  <BetaTag
                    v-if="feature.beta"
                    :message="$t(`release.versions[${latestReleaseIndex}].essential[${idx}].beta`)"
                  />
                </span>
              </div>
              <div
                v-if="feature.description"
                class="feature-description"
                v-html="$t(`release.versions[${latestReleaseIndex}].essential[${idx}].description`)"
              />
            </div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <!-- Options to close the modal, display other notes, ... -->
          <div class="bottom-options">
            <Checkbox
              :value="hideReleaseEssential"
              :title="$t('release.DONT_SHOW_ESSENTIAL_AGAIN')"
              :disabled="hideAllReleaseEssential"
              @input="SET_HIDE_RELEASE_ESSENTIAL"
            />
            <br>
            <Checkbox
              :value="hideAllReleaseEssential"
              :title="$t('release.DONT_SHOW_ANY_ESSENTIAL_AGAIN')"
              @input="SET_HIDE_ALL_RELEASE_ESSENTIAL"
            />
            <br>
            <button class="option-button" @click="displayAllNotes = true">
              {{ $t('release.SHOW_ALL_NOTES') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- All release notes -->
    <ReleaseNotes :displayed="displayAllNotes" @close="closeAllNotes" />
  </div>
</template>

<script>
/**
 * Component to inform the user about the latest changes made per version
 */

import { isEqual } from 'lodash';
// Vuex and utils
import { mapState, mapMutations } from 'vuex';
import { SET_HIDE_RELEASE_ESSENTIAL, SET_HIDE_ALL_RELEASE_ESSENTIAL } from '@/store/mutation-types';
// Components
import BetaTag from '@/components/beta-tag.vue';
import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';
import Icon from '@/components/icon.vue';
import ReleaseNotes from '@/components/release-notes.vue';
// Utils
import { giftBold } from '@/assets/icons';

export default {

  name: 'ReleaseNotesHandler',

  components: {
    BetaTag,
    Checkbox,
    CloseButton,
    Icon,
    ReleaseNotes,
  },

  data() {
    return {
      giftBold,
      displayEssentialNotes: false,
    };
  },

  computed: {
    /** Array: Array of version displayed in the list */
    releases() {
      return this.$t('release.versions');
    },
    /** Number: Index of the latest release (skipping unreleased features) */
    latestReleaseIndex() {
      for (let i = 0, len = this.releases.length; i < len; i++) {
        if (!this.releases[i].unreleased) {
          return i;
        }
      }
      return 0;
    },
    /** Array: Items in the 'essential' array of the latest available release */
    latestReleaseEssentials() {
      if (!this.releases || !this.releases.length || !this.currentTeam) {
        return null;
      }
      const essential = this.releases[this.latestReleaseIndex].essential || null;
      if (essential) {
        return essential.filter(feature => !feature.requireGW || this.currentTeam.accessGW === 1);
      }
      return null;
    },
    /** String: Version number X.X.X */
    latestReleaseVersion() {
      return this.releases && this.releases.length ? this.releases[this.latestReleaseIndex].version : '';
    },
    ...mapState([
      'version',
      'hideReleaseEssential',
      'hideAllReleaseEssential',
    ]),
    // ============
    // Query params
    // ============
    displayAllNotes: {
      get() {
        return this.$route.query.displayAllReleaseNotes === 'true';
      },
      set(doDisplay) {
        const query = Object.assign({}, this.$route.query);
        if (doDisplay) {
          query.displayAllReleaseNotes = 'true';
        } else {
          delete query.displayAllReleaseNotes;
        }
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  created() {
    setTimeout(this.checkEssentialFeatures, 1000);
  },

  watch: {
    currentTeam(newVal, oldVal) {
      if (newVal === null) {
        this.displayEssentialNotes = false;
      } else if (oldVal === null) {
        this.checkEssentialFeatures();
      }
    },
  },

  methods: {
    /**
     * When loading the page, if the user isn't aware of the newest essential features,
     * display a modal that can be dismissed and turned off from settings
     */
    checkEssentialFeatures() {
      // Check if there s any new essential features
      const { currentTeam, latestReleaseEssentials } = this;
      if (
        currentTeam
        && currentTeam.id
        && latestReleaseEssentials
        && latestReleaseEssentials.length
        && this.version === this.latestReleaseVersion
      ) {
        // Make sure the user hasn't asked to hide the modal
        if (!this.hideReleaseEssential && !this.hideAllReleaseEssential) {
          this.displayEssentialNotes = true;
        }
      }
    },
    /** Close the essential features modal */
    closeEssentialFeatures() {
      this.displayEssentialNotes = false;
    },
    /** Close the 'all notes' modal */
    closeAllNotes() {
      this.displayAllNotes = false;
    },
    ...mapMutations({
      SET_HIDE_RELEASE_ESSENTIAL,
      SET_HIDE_ALL_RELEASE_ESSENTIAL,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.release-notes-handler {

  // Essential features modal - Less items to display, so the texts can be bigger
  .essential-features.dark-overlay {
    .dark-overlay-content {
      text-align: left;
      display: flex;
      flex-flow: column nowrap;
      padding: 0px;
      height: auto;
      max-height: 90%;

      h2.basic-sub-title {
        margin: 0.8em 0px;
        line-height: 1.4em;
        font-size: 1.2em;
        position: relative;

        svg {
          position: absolute;
          color: $color-light-blue-text;
          width: 1.3em;
          height: 1.3em;
          margin-left: 1em;
        }
      }

      .notes-wrapper {
        height: auto;
        flex-grow: 1;
        padding: 2em 1.8em;
        text-align: center;

        .sep {
          margin: 1.8em 1em;
        }

        .feature-title {
          color: $color-blue-text;
          font-size: 1.1em;
          font-weight: 650;
          margin: 0.6em 0;

          span {
            position: relative;

            .beta-tag {
              position: absolute;
              top: 50%;
              transform: translateX(100%) translateY(-50%);
              right: -1em;
            }
          }
        }
        .feature-description {
          font-style: initial;
          font-size: 1em;

          ul {
            margin: 0.5em 1.5em;
          }
        }
      }
      .bottom-options {
        text-align: center;
        margin-bottom: 1em;
      }
    }
  }
}
</style>
